// src/components/privacy/FAQsPrivacyPolicy.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaPlus, 
  FaMinus, 
  FaShieldAlt,
  FaEnvelope,
  FaUserSecret,
  FaCookieBite,
  FaDatabase,
  FaUserLock
} from 'react-icons/fa';

const FAQsPrivacyPolicy = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Privacy policy related FAQs
  const faqs = [
    {
      icon: <FaShieldAlt />,
      question: "What personal information does StayIN collect?",
      answer: "We collect information you provide directly such as name, email address, phone number, payment details, and identification documents when making reservations. We also automatically collect usage data including IP address, browser type, and pages visited to improve our services."
    },
    {
      icon: <FaUserSecret />,
      question: "How is my personal information protected?",
      answer: "We implement industry-standard security measures including SSL encryption, secure servers, firewalls, and regular security audits. Access to personal information is restricted to authorized personnel only, and all payment transactions are processed through PCI-DSS compliant gateways."
    },
    {
      icon: <FaCookieBite />,
      question: "Does StayIN use cookies?",
      answer: "Yes, we use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie preferences through your browser settings. For more details, please see our Cookie Policy section."
    },
    {
      icon: <FaDatabase />,
      question: "How long do you retain my data?",
      answer: "We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Booking records are typically kept for 5 years for legal and accounting purposes."
    },
    {
      icon: <FaEnvelope />,
      question: "Will I receive marketing emails from StayIN?",
      answer: "We may send you promotional emails about special offers, new properties, or services if you have opted in to receive marketing communications. You can unsubscribe at any time by clicking the 'unsubscribe' link in any email or contacting us directly."
    },
    {
      icon: <FaUserLock />,
      question: "How can I access or delete my personal data?",
      answer: "You have the right to request access, correction, or deletion of your personal information. To exercise these rights, please contact our Data Protection Officer at info@stayinhospitality.com. We will respond to your request within 30 days as required by law."
    },
    {
      icon: <FaShieldAlt />,
      question: "Is my payment information secure?",
      answer: "Yes, all payment transactions are encrypted using SSL technology and processed through trusted third-party payment gateways that are PCI-DSS compliant. We do not store full credit card details on our servers."
    },
    {
      icon: <FaDatabase />,
      question: "Do you share my information with third parties?",
      answer: "We may share your information with trusted service providers who assist in operating our website, processing payments, and delivering services. We do not sell your personal information to third parties. Any third-party partners are contractually obligated to protect your data."
    }
  ];

  // Categories for filtering (optional)
  const categories = [
    { name: "All", icon: "🔍" },
    { name: "Data Collection", icon: "📋" },
    { name: "Security", icon: "🔒" },
    { name: "Your Rights", icon: "⚖️" },
    { name: "Cookies", icon: "🍪" }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
  };

  return (
    <section className="py-12 bg-gradient-to-b from-[#f1f1f1] via-[#f1f1f1]/0 to-[#f1f1f1]/0 relative overflow-hidden">

      <div className="container-custom max-w-4xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 space-y-2"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
            <span className="text-[#c9a86c] font-medium tracking-[0.2em] text-xs uppercase">
              Privacy FAQs
            </span>
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-light text-[#1a2b3c] !mb-4">
            Frequently Asked{' '}
            <span className="font-bold text-[#c9a86c]">Questions</span>
          </h2>
          
          <p className="text-gray-500 md:text-[17px] 2xl:text-lg max-w-2xl mx-auto">
            Find answers to common questions about how we handle your personal information and privacy
          </p>
        </motion.div>

        {/* FAQs Accordion */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white p-1 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100 overflow-hidden"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none group"
              >
                <div className="flex items-center gap-4">
                  {/* Icon with gradient background */}
                  <div className="flex-shrink-0 w-10 h-10 bg-[#c9a86c]/10 rounded-full flex items-center justify-center group-hover:bg-[#c9a86c] transition-colors duration-300">
                    <span className="text-[#c9a86c] group-hover:text-white transition-colors duration-300">
                      {faq.icon}
                    </span>
                  </div>
                  <span className="2xl:text-lg text-[15px] text-[#1a2b3c] font-medium">
                    {faq.question}
                  </span>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#c9a86c]/10 flex items-center justify-center">
                  {openIndex === index ? (
                    <FaMinus className="text-[#c9a86c] text-sm" />
                  ) : (
                    <FaPlus className="text-[#c9a86c] text-sm" />
                  )}
                </div>
              </button>

              {/* Answer (Conditional) */}
              {openIndex === index && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-5 pt-2 border-t border-gray-100"
                >
                  <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex-shrink-0"></div> {/* Spacer for alignment */}
                    <div className="flex-1">
                      <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                        {faq.answer}
                      </p>
                      
                      {/* Additional helpful links (optional) */}
                      {index === 4 && ( // For marketing emails FAQ
                        <div className="mt-3 flex items-center gap-3">
                          <a href="#unsubscribe" className="text-sm text-[#c9a86c] hover:underline">Unsubscribe</a>
                          <span className="text-gray-300">|</span>
                          <a href="#email-preferences" className="text-sm text-[#c9a86c] hover:underline">Email Preferences</a>
                        </div>
                      )}
                      
                      {index === 5 && ( // For data access FAQ
                        <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-[#1a2b3c] mb-1">Contact Data Protection Officer:</p>
                          <a href="mailto:info@stayinhospitality.com" className="text-sm text-[#c9a86c] hover:underline">info@stayinhospitality.com</a>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Still Have Questions? */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-6 2xl:mt-12 p-8 bg-gradient-to-b from-[#f1f1f1] via-[#f1f1f1]/0 to-[#f1f1f1]/0 mb-8 rounded-2xl border border-[#c9a86c]/20"
        >
          <h3 className="text-xl font-semibold text-[#1a2b3c] mb-2">Still have privacy questions?</h3>
          <p className="text-gray-600 mb-4">Our Data Protection Officer is here to help</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="mailto:info@stayinhospitality.com" 
              className="bg-[#c9a86c] text-white px-8 py-4 rounded-full font-medium hover:bg-[#b38f5a] transition-all duration-300 inline-flex items-center gap-2 justify-center group"
            >
              <FaEnvelope />
              info@stayinhospitality.com
            </a>
            <a 
              href="#contactus" 
              className="bg-transparent text-black border border-black px-8 py-4 rounded-full font-medium hover:bg-[#c9a86c] hover:text-white hover:border-none transition-all duration-300 inline-flex items-center gap-2 justify-center group"
            >
              Contact Form
            </a>
          </div>
        </motion.div>

        {/* Footer Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="md:text-[15px] text-center text-gray-600"
        >
          Last updated: March 15, 2026 · For complete details, please review our full Privacy Policy above
        </motion.p>
      </div>
    </section>
  );
};

export default FAQsPrivacyPolicy;